<script setup lang="ts">
import type { GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import type { RenderFieldDisplayProps } from './types';
import { shallowRef, watch } from 'vue';
import { usePrimitiveField, useSingleRelationField } from '#layers/data-model/public';

const { root = true, fieldId, value, item } = defineProps<RenderFieldDisplayProps>();

const primitiveField = usePrimitiveField(() => fieldId as GlobalPrimitiveFieldId);
const singleRelationField = useSingleRelationField(() => fieldId as GlobalSingleRelationFieldId);

/**
 * Keep a stable plain-object prop bag so recycled table rows do not force
 * child display components to diff on every parent scroll update.
 */
function createEffectiveProps(): RenderFieldDisplayProps {
	return {
		fieldId,
		value,
		item,
		root,
	};
}

const effectiveProps = shallowRef<RenderFieldDisplayProps>(createEffectiveProps());

watch(() => [fieldId, value, item, root] as const, (newValue, oldValue) => {
	if (oldValue
		&& newValue[0] === oldValue[0]
		&& Object.is(newValue[1], oldValue[1])
		&& newValue[2] === oldValue[2]
		&& newValue[3] === oldValue[3]) {
		return;
	}

	effectiveProps.value = createEffectiveProps();
});
</script>

<template>
	<RenderPrimitiveFieldDisplay v-if="primitiveField" v-bind="effectiveProps" :field="primitiveField" />
	<RenderRelationFieldDisplay v-else-if="singleRelationField" v-bind="effectiveProps" :field="singleRelationField" />
</template>
